import { useMutation, useQuery } from '@apollo/client/react';
import { PlusIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  CREATE_COMPANY_PRODUCT,
  type CreateCompanyProductResponse,
  DELETE_COMPANY_PRODUCT,
  type DeleteCompanyProductResponse,
  GET_COMPANY_OVERVIEW,
  type GetCompanyOverviewResponse,
  UPDATE_COMPANY_PRODUCT,
  type UpdateCompanyProductResponse
} from './graphql';
import { TableCard, type TableRow } from './section-ui';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import NumberField from '@/components/NumberField';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';

export function ProductsTab({
  dealOpportunityId
}: {
  dealOpportunityId: string;
}) {
  const { data: companyOverviewData, refetch: refetchCompanyOverview } =
    useQuery<GetCompanyOverviewResponse>(GET_COMPANY_OVERVIEW, {
      skip: !dealOpportunityId,
      variables: { deal_opportunity_id: dealOpportunityId }
    });

  const [createCompanyProduct, { loading: isCreatingProduct }] =
    useMutation<CreateCompanyProductResponse>(CREATE_COMPANY_PRODUCT);
  const [updateCompanyProduct, { loading: isUpdatingProduct }] =
    useMutation<UpdateCompanyProductResponse>(UPDATE_COMPANY_PRODUCT);
  const [deleteCompanyProduct, { loading: isDeletingProduct }] =
    useMutation<DeleteCompanyProductResponse>(DELETE_COMPANY_PRODUCT);

  const companyOverview = companyOverviewData?.companyOverviewByDeal;
  const productsRows: TableRow[] = (
    companyOverview?.companyProductsIds ?? []
  ).map(product => ({
    description: product.description,
    id: product.id,
    name: product.name,
    revenueShare: product.percentOfRevenue
  }));

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<null | TableRow>(null);
  const [deletingProductId, setDeletingProductId] = useState<null | string>(
    null
  );
  const [draft, setDraft] = useState<{
    description: string;
    name: string;
    revenueShare: string;
  }>({
    description: '',
    name: '',
    revenueShare: ''
  });

  useEffect(() => {
    if (isDialogOpen && !editingProduct) {
      setDraft({ description: '', name: '', revenueShare: '' });
    } else if (editingProduct) {
      setDraft({
        description: editingProduct.description ?? '',
        name: editingProduct.name ?? '',
        revenueShare:
          editingProduct.revenueShare != null
            ? String(editingProduct.revenueShare * 100)
            : ''
      });
    }
  }, [isDialogOpen, editingProduct]);

  const handleSaveProduct = async () => {
    if (!companyOverview) return;
    try {
      if (editingProduct) {
        const { data } = await updateCompanyProduct({
          variables: {
            id: editingProduct.id,
            values: {
              description: draft.description || null,
              name: draft.name || null,
              percentOfRevenue: draft.revenueShare
                ? parseFloat(draft.revenueShare) / 100
                : null
            }
          }
        });
        if (data?.updateCompanyProduct.ok) {
          toast.success('Product updated successfully');
          setIsDialogOpen(false);
          setEditingProduct(null);
          void refetchCompanyOverview();
        } else {
          toast.error(
            data?.updateCompanyProduct.errors[0] ?? 'Failed to update product'
          );
        }
      } else {
        const { data } = await createCompanyProduct({
          variables: {
            companyOverviewId: companyOverview.id,
            values: {
              description: draft.description || null,
              name: draft.name || null,
              percentOfRevenue: draft.revenueShare
                ? parseFloat(draft.revenueShare) / 100
                : null
            }
          }
        });
        if (data?.createCompanyProduct.ok) {
          toast.success('Product created successfully');
          setIsDialogOpen(false);
          void refetchCompanyOverview();
        } else {
          toast.error(
            data?.createCompanyProduct.errors[0] ?? 'Failed to create product'
          );
        }
      }
    } catch {
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    try {
      const { data } = await deleteCompanyProduct({
        variables: { id: productId }
      });
      if (data?.deleteCompanyProduct.ok) {
        toast.success('Product deleted successfully');
        setDeletingProductId(null);
        void refetchCompanyOverview();
      } else {
        toast.error(
          data?.deleteCompanyProduct.errors[0] ?? 'Failed to delete product'
        );
      }
    } catch {
      toast.error('Failed to delete product');
    }
  };

  return (
    <TableCard
      actions={
        <>
          <Dialog
            onOpenChange={open => {
              setIsDialogOpen(open);
              if (!open) setEditingProduct(null);
            }}
            open={isDialogOpen}
          >
            <DialogTrigger asChild>
              <Button>
                <PlusIcon />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle>
                  {editingProduct ? 'Edit Product' : 'New Product'}
                  <Badge variant="yellow">Unsaved</Badge>
                </DialogTitle>
              </DialogHeader>

              <DialogBody>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="products-name-field">Name</FieldLabel>
                    <Input
                      id="products-name-field"
                      onChange={event => {
                        setDraft(current => ({
                          ...current,
                          name: event.target.value
                        }));
                      }}
                      value={draft.name}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="products-revenue-field">
                      % of LTM Revenue
                    </FieldLabel>
                    <InputGroup>
                      <NumberField
                        id="products-revenue-field"
                        max={100}
                        min={0}
                        onChange={event => {
                          setDraft(current => ({
                            ...current,
                            revenueShare: event.target.value
                          }));
                        }}
                        value={draft.revenueShare}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupText>%</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="products-description-field">
                      Description
                    </FieldLabel>
                    <Textarea
                      id="products-description-field"
                      onChange={event => {
                        setDraft(current => ({
                          ...current,
                          description: event.target.value
                        }));
                      }}
                      value={draft.description}
                    />
                  </Field>
                </FieldGroup>
              </DialogBody>

              <DialogFooter>
                <Button
                  disabled={isCreatingProduct || isUpdatingProduct}
                  onClick={() => void handleSaveProduct()}
                  type="button"
                >
                  {(isCreatingProduct || isUpdatingProduct) && <Spinner />}
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <AlertDialog
            onOpenChange={open => {
              if (!open) setDeletingProductId(null);
            }}
            open={!!deletingProductId}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Product</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this product? This action
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={isDeletingProduct}
                  onClick={() => {
                    if (deletingProductId) {
                      void handleDeleteProduct(deletingProductId);
                    }
                  }}
                >
                  {isDeletingProduct && <Spinner />}
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      }
      emptyAction={
        <Button
          onClick={() => {
            setIsDialogOpen(true);
          }}
        >
          <PlusIcon />
          Add Product
        </Button>
      }
      onDelete={id => {
        setDeletingProductId(id);
      }}
      onEdit={row => {
        setEditingProduct(row);
        setIsDialogOpen(true);
      }}
      rows={productsRows}
      title="Products"
    />
  );
}
